import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';
import { cn } from '../../lib/utils';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'prefix'> {
  label?: string;
  error?: string;
  helperText?: string;
  /**
   * Repere compact affiche a l'interieur du champ (lettre, icone) — purement
   * visuel/decoratif (`aria-hidden`), jamais un substitut au `label`. Pour un
   * champ sans `label` visible (ex: deux champs cote a cote sous une seule
   * etiquette de groupe, `FieldRow`), fournir un `aria-label` explicite.
   */
  prefix?: ReactNode;
  /** 'sm' : champ compact (hauteur/texte reduits) pour les paires de champs numeriques d'un panneau de proprietes etroit. 'md' (defaut) inchange. */
  size?: 'sm' | 'md';
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, label, error, helperText, prefix, size = 'md', id, ...props }, ref) => {
    const inputId = id || label?.toLowerCase().replace(/\s+/g, '-');

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={inputId}
            className="block text-sm font-medium text-dark-500 mb-1.5"
          >
            {label}
          </label>
        )}
        <div className="relative flex items-center">
          {prefix && (
            <span
              aria-hidden="true"
              className={cn(
                'pointer-events-none absolute flex items-center text-neutral-400',
                size === 'sm' ? 'left-2 text-xs' : 'left-3 text-sm'
              )}
            >
              {prefix}
            </span>
          )}
          <input
            id={inputId}
            className={cn(
              'flex w-full rounded-lg border bg-white transition-colors',
              size === 'sm' ? 'h-8 py-1 text-xs' : 'h-10 py-2 text-sm',
              prefix ? (size === 'sm' ? 'pl-6 pr-2' : 'pl-8 pr-3') : size === 'sm' ? 'px-2' : 'px-3',
              'placeholder:text-neutral-400',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:border-primary-500',
              'disabled:cursor-not-allowed disabled:bg-neutral-100 disabled:text-neutral-400',
              error
                ? 'border-danger-500 focus-visible:ring-danger-500 focus-visible:border-danger-500'
                : 'border-neutral-300 hover:border-neutral-400',
              className
            )}
            ref={ref}
            aria-invalid={error ? 'true' : 'false'}
            aria-describedby={error ? `${inputId}-error` : helperText ? `${inputId}-helper` : undefined}
            {...props}
          />
        </div>
        {error && (
          <p id={`${inputId}-error`} className="mt-1.5 text-sm text-danger-500">
            {error}
          </p>
        )}
        {helperText && !error && (
          <p id={`${inputId}-helper`} className="mt-1.5 text-sm text-neutral-500">
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export { Input };
